import { defineComponent } from 'vue';
import { QIcon, QSpinner, QBtn, QTooltip } from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import { debounce } from 'lodash';
import utils from '../../../../../Shared/utils.jsx';
import FlowV2ListToolbar from './flowv2ListToolbar.jsx';
import {
    apiItemDescriptionText,
    apiItemHasDescription,
    apiOverloadLabel,
    apiOverloadTooltip,
    collapseApiOverloads,
} from './apiOverloads.js';

const BASE_URL = 'Apps/APIBrowser/ItemsAtNamespace';
const PLACEHOLDER_TYPE = 'ApiGridPlaceholder';

function normalizeArray(result) {
    if (Array.isArray(result))
        return result;
    if (result && typeof result === 'object') {
        if (Array.isArray(result.Items))
            return result.Items;
        if (Array.isArray(result.items))
            return result.items;
        const keys = Object.keys(result);
        if (keys.length && keys.every((k) => /^\d+$/.test(k)))
            return keys.sort((a, b) => Number(a) - Number(b)).map((k) => result[k]);
    }
    return [];
}

/** Search API nests children under `Items`; lazy/root responses may use `children` / `Children`. */
function childCollection(node) {
    if (!node || typeof node !== 'object')
        return null;
    if (Array.isArray(node.Items) && node.Items.length)
        return node.Items;
    if (Array.isArray(node.children) && node.children.length)
        return node.children;
    if (Array.isArray(node.Children) && node.Children.length)
        return node.Children;
    return null;
}

function dottedFromParent(parentDotted, displayName) {
    const seg = displayName == null ? '' : String(displayName).trim();
    if (!seg)
        return parentDotted != null && parentDotted !== '' ? String(parentDotted) : '';
    if (parentDotted != null && parentDotted !== '')
        return `${parentDotted}.${seg}`;
    return seg;
}

function pathIsStrictDescendantOf(rowPath, ancestorPath) {
    if (!rowPath || !ancestorPath || rowPath.length <= ancestorPath.length)
        return false;
    for (let i = 0; i < ancestorPath.length; i++) {
        if (rowPath[i] !== ancestorPath[i])
            return false;
    }
    return true;
}

function createPlaceholderRow(parentApiPath, parentNamespaceTreeNodeId) {
    const treeNodeId = utils.generateUUID();
    return {
        Type: PLACEHOLDER_TYPE,
        treeNodeId,
        apiPath: [...parentApiPath, treeNodeId],
        parentNamespaceTreeNodeId,
        Name: '',
        Key: null,
        childrenLoaded: true,
    };
}

function mapSearchHierarchy(nodes, parentKey, parentDotted) {
    const arr = normalizeArray(nodes);
    return collapseApiOverloads(arr.map((a) => {
        const mappedName =
            a.Type === 'Namespace' && parentKey != null && parentKey !== ''
                ? a.Key.substring(parentKey.length + 1)
                : a.Name;
        const dottedFullName = dottedFromParent(parentDotted, mappedName);
        const mapped = {
            ...a,
            Name: mappedName,
            dottedFullName,
            treeNodeId: utils.generateUUID(),
        };
        const kids = childCollection(a);
        if (kids && kids.length) {
            mapped.children = mapSearchHierarchy(kids, a.Key, dottedFullName);
            delete mapped.Items;
        }
        else {
            delete mapped.children;
            delete mapped.Items;
        }
        return mapped;
    }));
}

/** Flatten search hierarchy for tree data paths; all rows are fully loaded (no placeholders). */
function hierarchyToFlatRows(nodes, parentApiPath = []) {
    const out = [];
    if (!Array.isArray(nodes))
        return out;
    for (const n of nodes) {
        const { children, lazy, Items, ...rest } = n;
        const path = [...parentApiPath, n.treeNodeId];
        out.push({
            ...rest,
            apiPath: path,
            childrenLoaded: true,
        });
        if (children && children.length)
            out.push(...hierarchyToFlatRows(children, path));
    }
    return out;
}

/** Fixed-size host so refresh (namespace) and info (leaf) share the same right edge as folderGrid Menu column. */
const API_GRID_ACTION_HOST = {
    width: '36px',
    minWidth: '36px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
};

/** Right-aligned actions: namespace refresh (browse) + leaf description info (QTooltip on the info button). */
const ApiGridTreeActions = defineComponent({
    name: 'flow-v2-api-grid-tree-actions',
    components: { QSpinner, QBtn, QTooltip },
    methods: {
        onRefreshClick(e, row) {
            e.stopPropagation();
            e.preventDefault();
            const fn = this.params?.context?.refreshNamespace;
            if (typeof fn === 'function')
                fn(row);
        },
    },
    render() {
        const p = this.params;
        const data = p?.data;
        const ctx = p?.context || {};
        const shellStyle = {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            minHeight: '32px',
            paddingRight: '4px',
            boxSizing: 'border-box',
        };
        if (!data || data.Type === PLACEHOLDER_TYPE)
            return <div style={shellStyle} />;

        const isNs = data.Type === 'Namespace';
        const treeId = data.treeNodeId;
        const isSearch = typeof ctx.isSearchMode === 'function' ? ctx.isSearchMode() : false;

        if (!isNs) {
            const overloadTip = apiOverloadTooltip(data);
            const hasDesc = apiItemHasDescription(data);
            const tip = overloadTip || apiItemDescriptionText(data);
            const showInfo = !!overloadTip || hasDesc;
            const infoBtn = showInfo ? (
                <QBtn
                    size="sm"
                    flat
                    round
                    dense
                    color="grey-6"
                    icon="mdi-information-outline"
                    class="c-FlowV2-apiGrid-leafInfo"
                    style={{ cursor: 'help' }}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onMousedown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                >
                    <QTooltip
                        transition-show="scale"
                        transition-hide="scale"
                        anchor="top middle"
                        self="bottom middle"
                        class="text-body2"
                        style={{ maxWidth: '320px', whiteSpace: 'pre-line' }}
                    >
                        {tip}
                    </QTooltip>
                </QBtn>
            ) : (
                <QBtn
                    size="sm"
                    flat
                    round
                    dense
                    disable
                    tabindex={-1}
                    color="grey-5"
                    icon="mdi-information-outline"
                    class="c-FlowV2-apiGrid-leafInfo c-FlowV2-apiGrid-leafInfo--empty"
                    style={{ opacity: 0.45 }}
                />
            );
            return (
                <div style={shellStyle}>
                    <div style={API_GRID_ACTION_HOST}>{infoBtn}</div>
                </div>
            );
        }

        if (isSearch)
            return <div style={shellStyle} />;

        const isRefreshing = typeof ctx.isRefreshingNamespace === 'function' && ctx.isRefreshingNamespace(treeId);
        const isLazyExpanding = typeof ctx.isLazyExpanding === 'function' && ctx.isLazyExpanding(treeId);
        const showRefresh = !isRefreshing && !isLazyExpanding;

        return (
            <div style={shellStyle}>
                <div style={API_GRID_ACTION_HOST}>
                    {isRefreshing || isLazyExpanding ? (
                        <QSpinner color="primary" size="sm" />
                    ) : null}
                    {showRefresh ? (
                        <QBtn
                            size="sm"
                            flat
                            round
                            dense
                            color="grey-6"
                            icon="mdi-refresh"
                            class="c-FlowV2-apiGrid-refresh"
                            onClick={(e) => this.onRefreshClick(e, data)}
                            onMousedown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                        />
                    ) : null}
                </div>
            </div>
        );
    },
});

const ApiGridTreeInnerRenderer = defineComponent({
    name: 'flow-v2-api-grid-tree-inner',
    components: { QIcon, QSpinner },
    methods: {
        onDragStartLeaf(ev, row) {
            const fn = this.params?.context?.handleDragStartLeaf;
            if (typeof fn === 'function')
                fn(ev, row);
        },
    },
    render() {
        const p = this.params;
        const data = p?.data;
        const ctx = p?.context || {};
        if (!data)
            return null;
        if (data.Type === PLACEHOLDER_TYPE) {
            const loading = typeof ctx.isLazyExpanding === 'function' && ctx.isLazyExpanding(data.parentNamespaceTreeNodeId);
            return (
                <div class="row items-center" style={{ minHeight: '24px', paddingLeft: '4px' }}>
                    {loading ? <QSpinner size="xs" color="primary" /> : null}
                </div>
            );
        }

        const label = apiOverloadLabel(data);
        const isNamespace = data.Type === 'Namespace';
        const expanded = p.node?.expanded === true;
        const typeIcon = isNamespace
            ? (expanded ? 'mdi-folder-open' : 'mdi-folder-outline')
            : 'mdi-file-outline';
        const draggableLeaf = typeof ctx.isDraggableApiLeaf === 'function' && ctx.isDraggableApiLeaf(data);

        const labelStyle = {
            flex: '1 1 0',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
        };
        const labelEl = draggableLeaf ? (
            <span
                class="text-body1 tree-node"
                style={labelStyle}
                draggable={true}
                onDragstart={(e) => this.onDragStartLeaf(e, data)}
            >
                {label}
            </span>
        ) : (
            <span class="text-body1" style={labelStyle}>{label}</span>
        );

        return (
            <div
                style={{
                    flex: '1 1 auto',
                    width: '100%',
                    minWidth: 0,
                    minHeight: '32px',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                }}
            >
                <QIcon name={typeIcon} size="sm" class="q-mr-xs" style={{ flexShrink: 0 }} />
                {labelEl}
            </div>
        );
    },
});

export default defineComponent({
    name: 'flow-v2-api-grid',
    components: {
        AgGridVue,
        FlowV2ListToolbar,
    },
    data() {
        return {
            searchText: '',
            flatRows: [],
            isSearchMode: false,
            loadError: null,
            refreshNamespaceKey: null,
            lazyExpandNamespaceId: null,
            gridApi: null,
            gridComponents: {
                ApiGridTreeInnerRenderer,
                ApiGridTreeActions,
            },
            apiActionsColumnDefs: [
                {
                    colId: 'TreeActions',
                    valueGetter: () => '',
                    minWidth: 44,
                    maxWidth: 56,
                    sortable: false,
                    resizable: false,
                    cellRenderer: 'ApiGridTreeActions',
                },
            ],
            defaultColDef: {},
            autoGroupColumnDef: {
                colId: 'apiBrowserTree',
                field: 'Name',
                headerName: 'API',
                cellRenderer: 'agGroupCellRenderer',
                rowDrag: false,
                flex: 1,
                cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'ApiGridTreeInnerRenderer',
                },
            },
        };
    },
    computed: {
        apiGridContext() {
            return {
                isSearchMode: () => this.isSearchMode,
                isRefreshingNamespace: (id) => this.refreshNamespaceKey === id,
                isLazyExpanding: (id) => this.lazyExpandNamespaceId === id,
                refreshNamespace: (row) => this.refreshNamespace(row),
                handleDragStartLeaf: (ev, row) => this.handleDragStartLeaf(ev, row),
                isDraggableApiLeaf: (row) => this.isDraggableApiLeaf(row),
            };
        },
    },
    created() {
        this._debouncedApplySearch = debounce((trimmed) => {
            this.loadTree(trimmed);
        }, 300);
    },
    mounted() {
        this.loadTree('');
    },
    unmounted() {
        this._debouncedApplySearch?.cancel?.();
    },
    watch: {
        flatRows: {
            handler(newRows) {
                if (!this.isSearchMode || !newRows?.length || !this.gridApi)
                    return;
                this.expandAllSearchRows();
            },
        },
    },
    methods: {
        /** Tree row model may not be ready in a single tick after rowData updates; retry expand for search results. */
        expandAllSearchRows() {
            const api = this.gridApi;
            if (!api || !this.isSearchMode)
                return;
            const run = () => {
                try {
                    api.expandAll?.();
                }
                catch (_) {
                    /* noop */
                }
            };
            this.$nextTick(() => {
                run();
                requestAnimationFrame(run);
                setTimeout(run, 0);
                setTimeout(run, 50);
            });
        },
        gridReady(params) {
            this.gridApi = params.api;
            if (this.isSearchMode && this.flatRows.length)
                this.expandAllSearchRows();
        },
        refreshActionsColumn() {
            this.gridApi?.refreshCells?.({ columns: ['TreeActions'], force: true });
        },
        getDataPath(data) {
            return data.apiPath || [];
        },
        getRowId(params) {
            return String(params.data.treeNodeId);
        },
        buildRootFlatRows(shaped) {
            const flat = [];
            for (const row of shaped) {
                const apiPath = [row.treeNodeId];
                row.apiPath = apiPath;
                flat.push(row);
                if (row.Type === 'Namespace' && row.childrenLoaded === false)
                    flat.push(createPlaceholderRow(apiPath, row.treeNodeId));
            }
            return flat;
        },
        mapLoadedChildrenFlat(data, parentKey, parentDotted, parentApiPath) {
            const arr = normalizeArray(data);
            const addList = [];
            for (const a of arr) {
                const mappedName =
                    a.Type === 'Namespace' && parentKey != null && parentKey !== ''
                        ? a.Key.substring(parentKey.length + 1)
                        : a.Name;
                const dottedFullName = dottedFromParent(parentDotted, mappedName);
                const treeNodeId = utils.generateUUID();
                const mapped = {
                    ...a,
                    Name: mappedName,
                    dottedFullName,
                    treeNodeId,
                    apiPath: [...parentApiPath, treeNodeId],
                };
                if (mapped.Type === 'Namespace') {
                    mapped.childrenLoaded = false;
                    addList.push(mapped);
                    addList.push(createPlaceholderRow(mapped.apiPath, mapped.treeNodeId));
                }
                else {
                    mapped.childrenLoaded = true;
                    addList.push(mapped);
                }
            }
            return collapseApiOverloads(addList);
        },
        shapeRootRows(data) {
            const arr = normalizeArray(data);
            return collapseApiOverloads(arr.map((a) => {
                const dottedFullName = dottedFromParent('', a.Name);
                const treeNodeId = utils.generateUUID();
                const base = { ...a, dottedFullName, treeNodeId, Name: a.Name };
                if (a.Type === 'Namespace')
                    return { ...base, childrenLoaded: false };
                return { ...base, childrenLoaded: true };
            }));
        },
        findPlaceholderForNamespace(namespaceTreeNodeId) {
            return this.flatRows.find(
                (r) => r.Type === PLACEHOLDER_TYPE && r.parentNamespaceTreeNodeId === namespaceTreeNodeId,
            );
        },
        collectStrictDescendantRows(ancestorPath) {
            return this.flatRows.filter((r) => pathIsStrictDescendantOf(r.apiPath, ancestorPath));
        },
        syncFlatRowsRemove(rows) {
            if (!rows?.length)
                return;
            const removeIds = new Set(rows.map((r) => r.treeNodeId));
            this.flatRows = this.flatRows.filter((r) => !removeIds.has(r.treeNodeId));
        },
        syncFlatRowsAdd(rows) {
            if (!rows?.length)
                return;
            this.flatRows = [...this.flatRows, ...rows];
        },
        async loadTree(trimmedSearch) {
            this.loadError = null;
            try {
                if (trimmedSearch) {
                    const url = `${BASE_URL}?Search=${encodeURIComponent(trimmedSearch)}`;
                    const raw = await utils.api.get(url);
                    const hierarchy = normalizeArray(raw);
                    const mapped = mapSearchHierarchy(hierarchy, '', '');
                    this.isSearchMode = true;
                    this.flatRows = hierarchyToFlatRows(mapped, []);
                    this.expandAllSearchRows();
                }
                else {
                    const raw = await utils.api.get(BASE_URL);
                    this.isSearchMode = false;
                    const shaped = this.shapeRootRows(raw);
                    this.flatRows = this.buildRootFlatRows(shaped);
                }
            }
            catch (e) {
                utils.warn('ApiGrid failed to load ItemsAtNamespace', e);
                this.loadError = String(e?.message || e);
                this.flatRows = [];
            }
        },
        onSearchUpdate(v) {
            this.searchText = v ?? '';
            this._debouncedApplySearch((this.searchText || '').trim());
        },
        async onRowGroupOpened(params) {
            if (!params?.node?.expanded)
                return;
            const d = params.node.data;
            if (this.isSearchMode || !d || d.Type !== 'Namespace' || d.Key == null)
                return;
            if (d.childrenLoaded)
                return;
            if (this.lazyExpandNamespaceId === d.treeNodeId)
                return;
            const placeholder = this.findPlaceholderForNamespace(d.treeNodeId);
            if (!placeholder)
                return;
            this.lazyExpandNamespaceId = d.treeNodeId;
            this.refreshActionsColumn();
            try {
                const url = `${BASE_URL}?Namespace=${encodeURIComponent(d.Key)}`;
                const data = await utils.api.get(url);
                const parentDotted = d.dottedFullName != null ? String(d.dottedFullName) : '';
                const newRows = this.mapLoadedChildrenFlat(data, d.Key, parentDotted, d.apiPath);
                const toRemove = [placeholder];
                this.gridApi?.applyTransaction?.({ remove: toRemove, add: newRows });
                this.syncFlatRowsRemove(toRemove);
                this.syncFlatRowsAdd(newRows);
                d.childrenLoaded = true;
            }
            catch (e) {
                utils.warn(`ApiGrid lazy load failed for ${d.Key}`, e);
            }
            finally {
                this.lazyExpandNamespaceId = null;
                this.refreshActionsColumn();
            }
        },
        isDraggableApiLeaf(node) {
            if (!node || node.Type === 'Namespace' || node.Type === PLACEHOLDER_TYPE)
                return false;
            const dotted = node.dottedFullName != null ? String(node.dottedFullName).trim() : '';
            return dotted !== '';
        },
        handleDragStartLeaf(ev, node) {
            const dottedName = (node.dottedFullName != null && String(node.dottedFullName).trim())
                ? String(node.dottedFullName).trim()
                : (node.Name != null ? String(node.Name).trim() : '');
            ev.dataTransfer.effectAllowed = 'copy';
            ev.dataTransfer.setData('application/json', JSON.stringify({
                origin: 'FlowV2ApiBrowser',
                dottedName,
            }));
            ev.dataTransfer.setDragImage(ev.target, 10, 10);
        },
        onCellClicked(params) {
            if (params.column?.getColId?.() === 'TreeActions')
                return;
            const clickedExpand = !!params.event?.target?.closest?.('.ag-group-expanded, .ag-group-contracted');
            if (clickedExpand)
                return;
            if (params.node?.hasChildren?.())
                params.node.setExpanded(!params.node.expanded);
        },
        async refreshNamespace(node) {
            if (this.isSearchMode || node?.Type !== 'Namespace' || node.Key == null)
                return;
            if (this.refreshNamespaceKey === node.treeNodeId)
                return;
            if (!node.apiPath)
                return;
            this.refreshNamespaceKey = node.treeNodeId;
            this.refreshActionsColumn();
            try {
                const url = `${BASE_URL}?Namespace=${encodeURIComponent(node.Key)}`;
                const data = await utils.api.get(url);
                const parentDotted = node.dottedFullName != null ? String(node.dottedFullName) : '';
                const descendants = this.collectStrictDescendantRows(node.apiPath);
                const newRows = this.mapLoadedChildrenFlat(data, node.Key, parentDotted, node.apiPath);
                if (this.gridApi)
                    this.gridApi.applyTransaction({ remove: descendants, add: newRows });
                this.syncFlatRowsRemove(descendants);
                this.syncFlatRowsAdd(newRows);
                node.childrenLoaded = true;
            }
            catch (e) {
                utils.warn(`ApiGrid refresh failed for ${node.Key}`, e);
            }
            finally {
                this.refreshNamespaceKey = null;
                this.refreshActionsColumn();
            }
        },
    },
    render() {
        return (
            <div
                class="c-FlowV2-apiGrid ag-grid-noborder"
                style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}
            >
                <FlowV2ListToolbar
                    modelValue={this.searchText}
                    onUpdate:modelValue={(v) => this.onSearchUpdate(v)}
                />
                <AgGridVue
                    class="c-FlowV2-apiGrid-tree"
                    style={{
                        flex: '1 1 0',
                        minHeight: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#f2f2f2',
                    }}
                    headerHeight={0}
                    treeData={true}
                    groupDefaultExpanded={this.isSearchMode ? -1 : 0}
                    autoGroupColumnDef={this.autoGroupColumnDef}
                    defaultColDef={this.defaultColDef}
                    columnDefs={this.apiActionsColumnDefs}
                    rowData={this.flatRows}
                    getDataPath={this.getDataPath}
                    getRowId={this.getRowId}
                    onCellClicked={this.onCellClicked}
                    onRowGroupOpened={this.onRowGroupOpened}
                    components={this.gridComponents}
                    cellSelection={false}
                    context={this.apiGridContext}
                    rowDragManaged={false}
                    suppressMoveWhenRowDragging={true}
                    onGridReady={this.gridReady}
                />
                {this.loadError ? (
                    <div class="text-negative text-caption q-pa-sm">{this.loadError}</div>
                ) : null}
            </div>
        );
    },
});
